import { Controller } from 'react-hook-form'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import { Button } from '../../../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select'
import { Checkbox } from '../../../../components/checkbox'
import { Spinner } from '../../../../components/ui/spinner'
import { useCreateSellerModalController } from './useCreateSellerModalController'

interface CreateSellerModalProps {
  isOpen: boolean
  onClose: () => void
}

export function CreateSellerModal({ isOpen, onClose }: CreateSellerModalProps) {
  const {
    form,
    isPending,
    handleSubmit,
    handleCloseModal,
  } = useCreateSellerModalController({ onClose })

  const {
    register,
    control,
    formState: { errors },
  } = form

  return (
    <Dialog open={isOpen} onOpenChange={handleCloseModal}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar Novo Vendedor</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dados Pessoais */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Dados Pessoais</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="Nome completo"
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">CPF *</Label>
                <Input
                  id="cpf"
                  {...register('cpf')}
                  placeholder="000.000.000-00"
                />
                {errors.cpf && (
                  <p className="text-sm text-destructive">{errors.cpf.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="email@exemplo.com"
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefone *</Label>
                <Input
                  id="phone"
                  {...register('phone')}
                  placeholder="(11) 99999-9999"
                />
                {errors.phone && (
                  <p className="text-sm text-destructive">{errors.phone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthDate">Data de Nascimento</Label>
                <Input
                  id="birthDate"
                  type="date"
                  {...register('birthDate')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instagram">Instagram</Label>
                <Input
                  id="instagram"
                  {...register('instagram')}
                  placeholder="@usuario"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha *</Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                  placeholder="Mínimo 6 caracteres"
                />
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="teamCode">Código do Time</Label>
                <Input
                  id="teamCode"
                  {...register('teamCode')}
                  placeholder="Código opcional"
                />
              </div>
            </div>
          </div>

          {/* RG */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Documento RG</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rg.number">Número do RG</Label>
                <Input
                  id="rg.number"
                  {...register('rg.number')}
                  placeholder="12.345.678-9"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rg.organ">Órgão Emissor</Label>
                <Input
                  id="rg.organ"
                  {...register('rg.organ')}
                  placeholder="SSP"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rg.uf">UF</Label>
                <Input
                  id="rg.uf"
                  {...register('rg.uf')}
                  placeholder="SP"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Endereço</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address.zipCode">CEP *</Label>
                <Input
                  id="address.zipCode"
                  {...register('address.zipCode')}
                  placeholder="00000-000"
                />
                {errors.address?.zipCode && (
                  <p className="text-sm text-destructive">{errors.address.zipCode.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.street">Rua *</Label>
                <Input
                  id="address.street"
                  {...register('address.street')}
                  placeholder="Nome da rua"
                />
                {errors.address?.street && (
                  <p className="text-sm text-destructive">{errors.address.street.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.number">Número *</Label>
                <Input
                  id="address.number"
                  {...register('address.number')}
                  placeholder="123"
                />
                {errors.address?.number && (
                  <p className="text-sm text-destructive">{errors.address.number.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.complement">Complemento</Label>
                <Input
                  id="address.complement"
                  {...register('address.complement')}
                  placeholder="Apto 456"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.neighborhood">Bairro *</Label>
                <Input
                  id="address.neighborhood"
                  {...register('address.neighborhood')}
                  placeholder="Nome do bairro"
                />
                {errors.address?.neighborhood && (
                  <p className="text-sm text-destructive">{errors.address.neighborhood.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.city">Cidade *</Label>
                <Input
                  id="address.city"
                  {...register('address.city')}
                  placeholder="Nome da cidade"
                />
                {errors.address?.city && (
                  <p className="text-sm text-destructive">{errors.address.city.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address.state">Estado *</Label>
                <Input
                  id="address.state"
                  {...register('address.state')}
                  placeholder="SP"
                  maxLength={2}
                />
                {errors.address?.state && (
                  <p className="text-sm text-destructive">{errors.address.state.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Conta Bancária */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Conta Bancária</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bankAccount.accountType">Tipo de Conta *</Label>
                <Controller
                  name="bankAccount.accountType"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CC">Conta Corrente</SelectItem>
                        <SelectItem value="CP">Conta Poupança</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.bankAccount?.accountType && (
                  <p className="text-sm text-destructive">{errors.bankAccount.accountType.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankAccount.bank">Banco *</Label>
                <Input
                  id="bankAccount.bank"
                  {...register('bankAccount.bank')}
                  placeholder="Nome do banco"
                />
                {errors.bankAccount?.bank && (
                  <p className="text-sm text-destructive">{errors.bankAccount.bank.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankAccount.agency">Agência *</Label>
                <Input
                  id="bankAccount.agency"
                  {...register('bankAccount.agency')}
                  placeholder="0000"
                />
                {errors.bankAccount?.agency && (
                  <p className="text-sm text-destructive">{errors.bankAccount.agency.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bankAccount.account">Conta *</Label>
                <Input
                  id="bankAccount.account"
                  {...register('bankAccount.account')}
                  placeholder="00000-0"
                />
                {errors.bankAccount?.account && (
                  <p className="text-sm text-destructive">{errors.bankAccount.account.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Marketing */}
          <div className="flex items-center space-x-2">
            <Controller
              name="marketing"
              control={control}
              render={({ field }) => (
                <Checkbox
                  name="marketing"
                  label="Aceito receber comunicações de marketing"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              )}
            />
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseModal}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" className='text-white hover:text-black' disabled={isPending}>
              {isPending ? (
                <>
                  <Spinner className="mr-2" />
                  Criando...
                </>
              ) : (
                'Criar Vendedor'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}